import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Drawer } from 'vaul'
import {
  CheckCircle, Server, Wifi, Database, HardDrive, Wallet,
  Gauge, Cpu, MemoryStick, AlertTriangle, Clock,
  RefreshCw, FileSearch, ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { fadeInUp, cardReveal } from '@/lib/motion'

// -- Service status data --

interface ServiceData {
  name: string
  status: string
  color: 'emerald' | 'amber'
  icon: typeof Server
  metrics: { k: string; v: string }[]
  uptime: string
  responseTimeBars: number[]
  lastChecked: string
}

const services: ServiceData[] = [
  {
    name: 'API 服務', status: '正常', color: 'emerald',
    icon: Server, metrics: [{ k: '延遲', v: '45ms' }, { k: '可用率', v: '99.98%' }],
    uptime: '99.98% (30d)', responseTimeBars: [45, 42, 48, 44, 50, 43, 46], lastChecked: '2026-02-07 14:32:08',
  },
  {
    name: 'WebSocket', status: '正常', color: 'emerald',
    icon: Wifi, metrics: [{ k: '連線數', v: '47' }, { k: '延遲', v: '12ms' }],
    uptime: '99.97% (30d)', responseTimeBars: [12, 14, 11, 13, 15, 12, 10], lastChecked: '2026-02-07 14:32:05',
  },
  {
    name: '資料庫 (PostgreSQL)', status: '正常', color: 'emerald',
    icon: Database, metrics: [{ k: '查詢/秒', v: '234' }, { k: '連線池', v: '8/20' }],
    uptime: '99.99% (30d)', responseTimeBars: [8, 9, 7, 10, 8, 11, 9], lastChecked: '2026-02-07 14:32:02',
  },
  {
    name: 'Redis 快取', status: '正常', color: 'emerald',
    icon: HardDrive, metrics: [{ k: '記憶體', v: '128/512MB' }, { k: '命中率', v: '94%' }],
    uptime: '99.95% (30d)', responseTimeBars: [3, 4, 3, 5, 4, 6, 3], lastChecked: '2026-02-07 14:31:58',
  },
  {
    name: '錢包 API', status: '警告', color: 'amber',
    icon: Wallet, metrics: [{ k: '延遲', v: '890ms' }, { k: '可用率', v: '99.5%' }],
    uptime: '99.52% (30d)', responseTimeBars: [450, 890, 720, 650, 1100, 890, 780], lastChecked: '2026-02-07 14:32:10',
  },
]

const perfMetrics = [
  { label: 'API 延遲', value: '45ms', spark: [3, 5, 4, 6, 4], icon: Gauge },
  { label: 'WS 連線數', value: '47/200', spark: [2, 3, 5, 4, 5], icon: Wifi },
  { label: 'CPU 使用率', value: '34%', spark: [4, 3, 5, 3, 4], icon: Cpu },
  { label: '記憶體', value: '2.1/4GB', spark: [5, 5, 6, 5, 6], icon: MemoryStick },
]

interface Incident {
  time: string
  desc: string
  color: 'emerald' | 'amber'
  resolved: boolean
  details: string
  resolution: string
}

const incidents: Incident[] = [
  {
    time: '今日 14:32', desc: '錢包 API 延遲飆升至 1.2s', color: 'amber', resolved: false,
    details: '錢包 API 第三方服務回應時間從正常 200ms 飆升至 1200ms，觸發黃色警報。影響存提款操作約 15 筆交易。',
    resolution: '正在聯繫第三方服務商確認狀況，暫時啟用降級模式。',
  },
  {
    time: '今日 09:15', desc: 'WebSocket 短暫斷線 (12 秒)', color: 'emerald', resolved: true,
    details: 'WebSocket 伺服器因記憶體回收觸發短暫斷線，持續 12 秒，影響 23 位線上玩家。所有連線在 5 秒內自動重新建立。',
    resolution: '已調整 GC 參數並增加 heap size 至 2GB，預防未來發生。',
  },
  {
    time: '昨日 22:48', desc: 'Redis 記憶體使用率超過 80%', color: 'emerald', resolved: true,
    details: 'Redis 快取記憶體使用率達到 82% (420/512MB)，觸發黃色警報。主要原因為過期 session key 累積。',
    resolution: '已執行手動清理過期 key，記憶體降至 55%。已設定自動清理排程每 4 小時執行。',
  },
]

// -- Tiny SVG spark line --

function SparkLine({ points }: { points: number[] }) {
  const max = Math.max(...points)
  const coords = points.map((p, i) => `${i * 14},${24 - (p / max) * 20}`).join(' ')
  return (
    <svg width="56" height="24" className="text-gold opacity-60">
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={coords}
      />
    </svg>
  )
}

// -- Simple bar chart for drawer --

function ResponseTimeChart({ bars, color }: { bars: number[]; color: 'emerald' | 'amber' }) {
  const max = Math.max(...bars)
  const barColor = color === 'emerald' ? 'bg-emerald-400' : 'bg-amber-400'
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">回應時間 (最近 7 次檢測)</p>
      <div className="flex items-end justify-between gap-1.5 h-20">
        {bars.map((val, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[8px] text-muted-foreground font-mono">{val}ms</span>
            <div
              className={cn('w-full rounded-t-sm transition-all', barColor)}
              style={{ height: `${(val / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// -- Color helpers --

const dot: Record<string, string> = { emerald: 'bg-emerald-500', amber: 'bg-amber-500' }
const badge: Record<string, string> = {
  emerald: 'bg-emerald-500/10 text-emerald-400',
  amber: 'bg-amber-500/10 text-amber-400',
}

// -- Screen --

export function AdminHealthScreen() {
  const [selectedService, setSelectedService] = useState<ServiceData | null>(null)
  const [rechecking, setRechecking] = useState(false)
  const [expandedIncident, setExpandedIncident] = useState<number | null>(null)
  const navigate = useNavigate()

  function handleRecheck() {
    setRechecking(true)
    setTimeout(() => {
      setRechecking(false)
      setSelectedService(null)
    }, 1500)
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-8 flex flex-col gap-5"
    >
      {/* Overall Status Banner */}
      <motion.div
        variants={fadeInUp}
        className="p-5 rounded-2xl glass-card flex items-center gap-4 border border-emerald-500/20"
      >
        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
        </div>
        <div className="flex-1">
          <p className="text-base font-bold text-emerald-400">所有系統正常運行</p>
          <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
            <Clock className="w-3 h-3" /> 最後檢查: 30 秒前
          </p>
        </div>
      </motion.div>

      {/* Service Status Cards */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">服務狀態</h3>
        {services.map((s) => (
          <motion.button
            key={s.name}
            variants={cardReveal}
            onClick={() => setSelectedService(s)}
            className="w-full text-left flex items-center gap-3 p-3.5 rounded-xl glass-card active:scale-[0.98] transition-transform"
          >
            <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', badge[s.color])}>
              <s.icon className="w-4.5 h-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dot[s.color])} />
                <span className="text-xs font-bold text-foreground truncate">{s.name}</span>
                <span className={cn('ml-auto text-[10px] font-bold px-2 py-0.5 rounded-md flex-shrink-0', badge[s.color])}>
                  {s.status}
                </span>
              </div>
              <div className="flex gap-3 mt-1 pl-3.5">
                {s.metrics.map((m) => (
                  <span key={m.k} className="text-[10px] text-muted-foreground">
                    {m.k}: <span className="font-mono text-foreground">{m.v}</span>
                  </span>
                ))}
              </div>
            </div>
          </motion.button>
        ))}
      </motion.section>

      {/* Performance Metrics Grid */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">效能指標</h3>
        <div className="grid grid-cols-2 gap-3">
          {perfMetrics.map((m) => (
            <motion.div key={m.label} variants={cardReveal} className="p-3.5 rounded-2xl glass-card space-y-2">
              <div className="flex items-center justify-between">
                <div className="w-7 h-7 rounded-lg bg-foreground/5 flex items-center justify-center text-muted-foreground">
                  <m.icon className="w-3.5 h-3.5" />
                </div>
                <SparkLine points={m.spark} />
              </div>
              <div>
                <p className="text-lg font-bold font-mono text-foreground">{m.value}</p>
                <p className="text-[10px] text-muted-foreground">{m.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Recent Incidents */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">近期事件</h3>
        {incidents.map((inc, i) => {
          const isExpanded = expandedIncident === i
          return (
            <motion.div key={i} variants={cardReveal} className="rounded-xl glass-card overflow-hidden">
              <button
                onClick={() => setExpandedIncident(isExpanded ? null : i)}
                className="w-full text-left flex items-start gap-3 p-3"
              >
                <div className="w-8 h-8 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertTriangle className={cn('w-4 h-4', inc.color === 'amber' ? 'text-amber-400' : 'text-emerald-400')} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dot[inc.color])} />
                    <p className="text-xs font-bold text-foreground truncate">{inc.desc}</p>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] text-muted-foreground/60">{inc.time}</span>
                    <span className={cn(
                      'text-[10px] font-bold px-2 py-0.5 rounded-md',
                      inc.resolved ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400',
                    )}>
                      {inc.resolved ? '已解決' : '處理中'}
                    </span>
                  </div>
                </div>
                <ChevronDown className={cn(
                  'w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )} />
              </button>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pt-0 border-t border-foreground/10 mx-3 space-y-2">
                      <p className="text-[11px] text-foreground/80 leading-relaxed pt-2">{inc.details}</p>
                      <div>
                        <span className="text-[10px] text-muted-foreground">處理方式: </span>
                        <span className="text-[10px] text-foreground">{inc.resolution}</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </motion.section>

      {/* Service Detail Drawer */}
      <Drawer.Root open={selectedService !== null} onOpenChange={(open) => { if (!open) setSelectedService(null) }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh]">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 overflow-y-auto max-h-[85vh] scrollbar-hide">
              <div className="flex justify-center py-3 sticky top-0 bg-surface-elevated z-10">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              {selectedService && (
                <div className="px-5 pb-8 safe-area-bottom space-y-5">
                  <Drawer.Title className="sr-only">{selectedService.name} 服務詳情</Drawer.Title>

                  {/* Service Header */}
                  <div className="flex items-center gap-4">
                    <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', badge[selectedService.color])}>
                      <selectedService.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-foreground">{selectedService.name}</h2>
                        <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold', badge[selectedService.color])}>
                          {selectedService.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        可用率: <span className="font-mono text-foreground">{selectedService.uptime}</span>
                      </p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-3">
                    {selectedService.metrics.map((m) => (
                      <div key={m.k} className="p-3 rounded-xl bg-surface-elevated text-center">
                        <p className="text-[10px] text-muted-foreground mb-1">{m.k}</p>
                        <p className="text-sm font-bold font-mono text-foreground">{m.v}</p>
                      </div>
                    ))}
                  </div>

                  {/* Response Time Chart */}
                  <div className="p-4 rounded-xl bg-surface-elevated">
                    <ResponseTimeChart bars={selectedService.responseTimeBars} color={selectedService.color} />
                  </div>

                  {/* Last Checked */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    <span>最後檢查: <span className="font-mono text-foreground">{selectedService.lastChecked}</span></span>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <button
                      onClick={handleRecheck}
                      disabled={rechecking}
                      className="flex items-center justify-center gap-2 py-3 rounded-xl glass-card active:scale-95 transition-transform disabled:opacity-50"
                    >
                      <RefreshCw className={cn('w-4 h-4 text-emerald-400', rechecking && 'animate-spin')} />
                      <span className="text-xs font-bold text-emerald-400">
                        {rechecking ? '檢測中...' : '重新檢測'}
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedService(null)
                        navigate('/admin/audit')
                      }}
                      className="flex items-center justify-center gap-2 py-3 rounded-xl glass-card active:scale-95 transition-transform"
                    >
                      <FileSearch className="w-4 h-4 text-blue-400" />
                      <span className="text-xs font-bold text-blue-400">查看日誌</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
